import { LocationStrategy } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params } from '@angular/router';
import * as moment from 'moment';
import { Subscription, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { ProductTypeName } from 'src/app/models/enums/product-type.enum';
import { LanguageModel } from 'src/app/models/language.model';
import { IPartnerConfig } from 'src/app/models/partner-config.model';
import { ProductOptionModel } from 'src/app/models/product-option.model';
import { ProductModel } from 'src/app/models/product.model';
import { CartProductInfo, CartProductInfoProvider } from 'src/app/providers/cart-product-info.provider';
import { BookingService } from 'src/app/services/booking.service';
import { NavigationService } from 'src/app/services/navigation.service';

@Component({
  selector: 'app-options-list',
  templateUrl: './options-list.component.html',
  styleUrls: ['./options-list.component.scss'],
})
export class OptionsListComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('navbar') navbar;

  public products: ProductModel[] = [];
  public languages: LanguageModel[];
  public options: ProductOptionModel[];
  public filter: FormGroup;
  public interval: any;
  public loaded: boolean;
  public pageConfig: IPartnerConfig;
  public baseUrl: string = '/';

  private subscribtions: Subscription;
  constructor(
    private fb: FormBuilder,
    public booking: BookingService,
    private route: ActivatedRoute,
    private title: Title,
    private meta: Meta,
    private locationStrategy: LocationStrategy,
    private navigationService: NavigationService,
    private productInfoProvider: CartProductInfoProvider
  ) {
    // Prepending location.origin here so that it works with Thumbor.
    this.baseUrl = location.origin + this.locationStrategy.getBaseHref();
  }

  public ngOnInit() {
    this.subscribtions = new Subscription();

    this.loaded = false;

    this.navigationService.storeQueryParams(this.route.snapshot.queryParams);
    this.subscribtions.add(
      zip(this.booking.getProducts(), this.route.queryParams)
        .pipe(
          map(([products, queryParams]) => {
            products = products.map((product) => this.setImages(product));
            return [products, queryParams];
          }),
          tap<[ProductModel[], Params]>(([products, queryParams]) => {
            const allOptions = [].concat.apply(
              [],
              products.map((product) =>
                product.options
                  .map((option) => {
                    option.product = product;
                    return option;
                  })
                  .sort((a, b) => a.order - b.order)
              )
            );
            this.options = allOptions;
            if (queryParams && queryParams.options) {
              const optionsToShow = queryParams.options.split(',');
              this.options = this.options.filter((o) => optionsToShow.some((id) => +id === +o.id));
            }
            this.title.setTitle(products[0].name);
            this.storeProductInfo(products);
          }),
          switchMap(() => {
            return this.booking.getPartnerCustomization(this.products[0].partnerId);
          })
        )
        .subscribe(
          (partnerConfig: IPartnerConfig) => {
            this.applyConfig(partnerConfig);
            this.loaded = true;
          },
          () => {
            this.loaded = true;
          }
        )
    );

    this.subscribtions.add(this.booking.getLanguages().subscribe((resp: LanguageModel[]) => (this.languages = resp)));

    const begin = new Date();
    const end = new Date();

    end.setDate(begin.getDate() + 2);
    this.filter = this.fb.group({
      date: [{ begin: moment(begin), end: moment(end) }, this.validateInterval],
    });
  }

  private storeProductInfo(products: ProductModel[]) {
    const product = products[0];
    let productInfo: CartProductInfo = {
      productIds: products.map((product) => product.id),
      partnerPaymentSystemId: product.paymentSystemId,
      currency: product.currency,
      productTypeId: product.productTypeId,
      widgetContext: 'list',
    };

    this.productInfoProvider.store(productInfo);
  }

  public ngAfterViewInit() {
    this.setPeriod();
  }

  public ngOnDestroy(): void {
    this.subscribtions.unsubscribe();
  }

  public setPeriod() {
    if (this.filter.valid) {
      this.booking.setInterval({ ...this.filter.controls.date.value });
    }
  }

  public trackByFn(option) {
    return option.id;
  }

  public applyConfig(config: IPartnerConfig) {
    this.pageConfig = { ...config };
    this.title.setTitle(this.pageConfig.title);
    this.meta.addTag({ name: 'description', content: this.pageConfig.description });
  }

  private validateInterval(control) {
    const { begin, end } = control.value;
    const today = moment();

    return begin.endOf('day').isBefore(today) || end.endOf('day').isBefore(today) ? { isBeforeToday: true } : null;
  }

  private setImages(product: ProductModel): ProductModel {
    return {
      ...product,
      imageUrl:
        product.imageUrl ||
        `${this.baseUrl}assets/images/compressed/${ProductTypeName[product.productTypeId]}-head.jpg`,
      options: product.options.map((option: ProductOptionModel) => ({
        ...option,
        imageUrl:
          option.imageUrl ||
          `${this.baseUrl}assets/images/compressed/${ProductTypeName[product.productTypeId]}-default.jpg`,
      })),
    };
  }
}
